import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useAppDispatch } from '@dealstack/frontend/app/hooks/redux';
import { setShouldDisableIsContactPerson } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MipManage.slice';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { ProgrammeUserWithPermissionRolesRes } from '@dealstack/shared/lib/generated';

interface Options
	extends Omit<
		UseQueryOptions<
			ApiV1.Response<ProgrammeUserWithPermissionRolesRes[]>,
			unknown,
			ProgrammeUserWithPermissionRolesRes[],
			[string, { programmeId: string }]
		>,
		'queryKey' | 'queryFn' | 'select'
	> {
	programmeId: string;
}

const getProgrammeUsers = async (endpoint: string, args: { programmeId: string }) => {
	const { programmeId } = args;
	return await makeRequest<ApiV1.Response<ProgrammeUserWithPermissionRolesRes[]>>({
		endpoint: formatUnicorn(endpoint, { programmeId }),
		method: HTTP_METHOD.GET,
	});
};

export const useProgrammeUsers = (
	options: Options
): UseQueryResult<ProgrammeUserWithPermissionRolesRes[] | undefined, unknown> => {
	const { programmeId, ...queryOptions } = options;
	const dispatch = useAppDispatch();

	const select = (data: ApiV1.Response<ProgrammeUserWithPermissionRolesRes[]>) => {
		const contactPersonCount = data.payload?.filter((user) => user.isContactPerson).length ?? 0;
		const shouldDisableIsContactPerson = contactPersonCount <= 1;
		dispatch(setShouldDisableIsContactPerson(shouldDisableIsContactPerson));

		return data.payload;
	};

	return useQuery({
		queryKey: [API_PATHS.MIP_PROGRAMMEID_USERS_GET, { programmeId }],
		queryFn: () => getProgrammeUsers(API_PATHS.MIP_PROGRAMMEID_USERS_GET, { programmeId }),
		select,
		...queryOptions,
	});
};
